<script setup lang="ts">
/** Takes a photo with the computer camera, like WhatsApp Web's "Câmera", and hands it over as a JPEG. */
import { onBeforeUnmount, onMounted, ref } from "vue";
import Modal from "./Modal.vue";
import Button from "./Button.vue";
import Icon from "./Icon.vue";
import { t } from "../i18n";

const emit = defineEmits<{ close: []; capture: [file: File] }>();
const video = ref<HTMLVideoElement>();
const error = ref("");
const ready = ref(false);
let stream: MediaStream | null = null;

onMounted(async () => {
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: false,
    });
    if (video.value) {
      video.value.srcObject = stream;
      await video.value.play().catch(() => undefined);
      ready.value = true;
    }
  } catch {
    error.value = t("camera.denied");
  }
});

function stop() {
  stream?.getTracks().forEach((track) => track.stop());
  stream = null;
}

async function shoot() {
  const el = video.value;
  if (!el || !el.videoWidth) return;
  const canvas = document.createElement("canvas");
  canvas.width = el.videoWidth;
  canvas.height = el.videoHeight;
  const ctx = canvas.getContext("2d")!;
  // The preview is mirrored like a selfie; the photo is not, as in WhatsApp.
  ctx.drawImage(el, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.9),
  );
  if (!blob) return;
  const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
  stop();
  emit(
    "capture",
    new File([blob], `foto-${stamp}.jpg`, { type: "image/jpeg" }),
  );
}

function close() {
  stop();
  emit("close");
}

onBeforeUnmount(stop);
</script>

<template>
  <Modal :open="true" :title="t('camera.title')" wide @close="close">
    <div class="camera-capture">
      <video
        v-show="!error"
        ref="video"
        class="camera-video"
        autoplay
        muted
        playsinline
      />
      <div v-if="error" class="camera-error">
        <Icon name="camera" :size="40" />
        <p>{{ error }}</p>
      </div>
    </div>
    <template #footer>
      <Button variant="secondary" @click="close">{{
        t("common.cancel")
      }}</Button>
      <Button icon="camera" :disabled="!ready || !!error" @click="shoot">{{
        t("camera.shoot")
      }}</Button>
    </template>
  </Modal>
</template>
